import React, { CSSProperties, useEffect, useState } from 'react'
import Table, { Column } from '@paytrace/pt-skel/Component/Table'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import Event from '../Event'
import { Fieldset, Legend, Loader, PrimaryButton } from '@paytrace/pt-skel/Component'
import { CHANGE_LOG_RECEIPT, CHANGE_LOG_WEEKENDS, ChangeLogHistoryResponse, ChangeLogReceiptMap, ChangeLogSurchargeMap, ChangeLogWeekendsMap, RecurChangeLogData, RecurFormData, RecurFrequencyMap } from '../types'
import { formatMoney } from '../Helper/formatMoney'

type RecurringChangeLog = {
    currentRecord: RecurFormData
    showSurchargeInfo: Boolean,
    setIsChangeLogButtonClicked: (value: boolean) => void
}

export const summaryTableStyle: { [key: string]: CSSProperties } = {
  container: {
    width: '100%',
    border: '1px solid lightgrey',
    fontSize: '1rem',
    wordWrap: 'break-word',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
  },
  th: {
    color: '#196FAB',
    fontWeight: 'bold',
    padding: '12px 10px',
    borderBottom: '1px solid #ddd',
    textAlign: 'left',
    fontSize: '1rem',
    wordWrap: 'break-word',
  },
  td: {
    padding: '12px 10px',
    borderBottom: '1px solid #ddd',
    verticalAlign: 'top',
    textAlign: 'left',
    wordWrap: 'break-word',
  },
  oddRow: {
    backgroundColor: '#F3F3F3',
  },
}

const mapData = (data: ChangeLogHistoryResponse[]): RecurChangeLogData[] => {
  return data.map((data) => {
    const foundfrequency = RecurFrequencyMap.find(item => item.val === data.frequency)
    const frequency = foundfrequency ? foundfrequency.name : ' '

    const foundWeekend = ChangeLogWeekendsMap.find(item => item.val === data.process_on_weekend)
    const process_on_weekend = foundWeekend ? foundWeekend.name : ' '

    const foundReceipt = ChangeLogReceiptMap.find(item => item.val === data.customer_receipt)
    const send_customer_recurring_receipt = foundReceipt ? foundReceipt.name : ' '

    const foundSurcharge = ChangeLogSurchargeMap.find(item => item.val === data.add_surcharge)
    const apply_surcharge = foundSurcharge ? foundSurcharge.name : ' '
    const customer_link = 'mcust.pay?cid=' + data.customer_id
    const customer_name = `${data.customer_custid} / ${data.customer_billing_name}`

    return {
      customer_link,
      customer_name,
      apply_surcharge,
      add_surcharge: data.add_surcharge,
      amount: formatMoney(data.amount),
      current_count: data.current_count,
      customer_id: data.customer_custid,
      send_customer_recurring_receipt,
      frequency,
      merchant_id: data.merchant_id,
      ip_address: data.ip_address,
      next_processing_date: data.next_processing_date,
      number_of_retries_if_declined: data.repeat,
      recurring_id: data.recurring_id,
      recurring_change_log_id: data.recurring_log_id,
      sec_code: data.sec_code,
      user_that_made_last_change: data.username,
      total_count: data.total_count,
      transaction_type: data.transaction_type,
      process_on_weekend,
      date_changed: data.when,
    }
  })
}

const mapCurrentRecord = (currentRecord: RecurFormData): RecurChangeLogData[] => {
  const foundfrequency = RecurFrequencyMap.find(item => item.val === currentRecord.frequency)
  const frequency = foundfrequency ? foundfrequency.name : ' '
  let process_on_weekend; let send_customer_recurring_receipt = ''

  if (currentRecord.process_on_weekends) {
    process_on_weekend = CHANGE_LOG_WEEKENDS.YES
  } else {
    process_on_weekend = CHANGE_LOG_WEEKENDS.NO
  }

  if (currentRecord.customer_receipt) {
    send_customer_recurring_receipt = CHANGE_LOG_RECEIPT.YES
  } else {
    send_customer_recurring_receipt = CHANGE_LOG_RECEIPT.NO
  }
  const foundSurcharge = ChangeLogSurchargeMap.find(item => item.val === currentRecord.add_surcharge)
  const apply_surcharge = foundSurcharge ? foundSurcharge.name : ''
  const customer_name = `${currentRecord?.customer_custid} \ ${currentRecord?.customer_billing_name}`
  const customer_link = `mcust.pay?cid=${currentRecord?.customer_id}`
  const result = []
  const current_recurring = {
    customer_link,
    customer_name,
    apply_surcharge,
    add_surcharge: currentRecord.add_surcharge,
    amount: formatMoney(currentRecord.amount),
    current_count: Number(currentRecord.no_of_payment_processed),
    customer_id: currentRecord.customer_id,
    send_customer_recurring_receipt,
    frequency,
    merchant_id: Number(currentRecord.merchant_id),
    ip_address: currentRecord.ip_address,
    next_processing_date: currentRecord.next_date,
    number_of_retries_if_declined: Number(currentRecord.no_of_attempts_if_declined),
    recurring_id: currentRecord.id,
    recurring_change_log_id: null,
    sec_code: currentRecord.sec_code,
    user_that_made_last_change: currentRecord.username,
    total_count: Number(currentRecord.transaction_count),
    transaction_type: currentRecord.transaction_type[0],
    process_on_weekend,
    date_changed: currentRecord.when,
  }
  result.push(current_recurring)
  return result
}
const RecurringChangeLog = ({ currentRecord, showSurchargeInfo, setIsChangeLogButtonClicked }: RecurringChangeLog) => {
  const [loadingData, setloadingData] = useState(true)
  const [changeLogData, setChangeLogData] = useState<RecurChangeLogData[]>([])
  const [currentRecordProcessed, setCurrentRecordProcessed] = useState<RecurChangeLogData[]>([])
  const surchargeStar = showSurchargeInfo ? '*' : ''
  useEffect(() => {
    const handleResponse = (data: ChangeLogHistoryResponse[]) => {
      setloadingData(false)
      if (data && Array.isArray(data)) {
        const processedData = mapData(data)
        const processedCurrentRecord = mapCurrentRecord(currentRecord)
        setChangeLogData(processedData)
        setCurrentRecordProcessed(processedCurrentRecord)
      }
    }

    PubSub.sub(self, Event.GET_RECURRING_CHANGE_LOG_RESPONSE, handleResponse)

    return () => {
      PubSub.unsub(Event.GET_RECURRING_CHANGE_LOG_RESPONSE)
    }
  }, [currentRecord])

  const recurringChangeLogColumns: Column[] = [
    {
      field: 'CUSTID',
      headerName: 'Customer ID',
      cellRender: (_: any, row: RecurChangeLogData) => (
        <div>
            <a href={row.customer_link}>{row.customer_name}</a>
        </div>
      ),
    },
    {
      field: 'AMNT',
      headerName: `${surchargeStar}Amount`,
      cellRender: (_: any, row: RecurChangeLogData) => (
        <div>
            {row.amount}
        </div>
      ),
    },
    {
      field: 'FREQUENCY',
      headerName: 'Frequency',
      cellRender: (_: any, row: RecurChangeLogData) => (
        <div>
            {row.frequency}
        </div>
      ),
    },
    {
      field: 'TOTALCOUNT',
      headerName: 'Total',
      cellRender: (_: any, row: RecurChangeLogData) => (
        <div>
            {row.total_count}
        </div>
      ),
    },
    {
      field: 'CURCOUNT',
      headerName: 'Current',
      cellRender: (_: any, row: RecurChangeLogData) => (
        <div>
            {row.current_count}
        </div>
      ),
    },
    {
      field: 'NEXT',
      headerName: 'Next Date',
      cellRender: (_: any, row: RecurChangeLogData) => (
        <div>
            {String(row.next_processing_date)}
        </div>
      ),
    },
    {
      field: 'TRANXTYPE',
      headerName: 'Type',
      cellRender: (_: any, row: RecurChangeLogData) => (
        <div>
            {row.transaction_type}
        </div>
      ),
    },
    {
      field: 'SEC_CODE',
      headerName: 'SEC Code',
      cellRender: (_: any, row: RecurChangeLogData) => (
            <div>
                {row.sec_code}
            </div>
      ),
    },
    {
      field: 'CUSTRECEIPT',
      headerName: 'Receipt',
      cellRender: (_: any, row: RecurChangeLogData) => (
            <div>
                {row.send_customer_recurring_receipt}
            </div>
      ),
    },
    {
      field: 'WEEKEND',
      headerName: 'Weekends?',
      cellRender: (_: any, row: RecurChangeLogData) => (
            <div>
                {row.process_on_weekend}
            </div>
      ),
    },
    {
      field: 'REPEAT',
      headerName: 'Repeat',
      cellRender: (_: any, row: RecurChangeLogData) => (
            <div>
                {row.number_of_retries_if_declined}
            </div>
      ),
    },
    ...(showSurchargeInfo
      ? [
          {
            field: 'surcharge',
            headerName: 'Surcharge',
            cellRender: (_: any, row: RecurChangeLogData) => (
              <div>
                  {row.apply_surcharge}
              </div>),
          },
        ]
      : []),
    {
      field: 'WHEN',
      headerName: 'Date/Time',
      cellRender: (_: any, row: RecurChangeLogData) => (
            <div>
                {row.date_changed}
            </div>
      ),
    },
    {
      field: 'SERVER',
      headerName: 'User',
      cellRender: (_: any, row: RecurChangeLogData) => (
            <div>
                {row.user_that_made_last_change}
            </div>
      ),
    },
    {
      field: 'IP',
      headerName: 'Source',
      cellRender: (_: any, row: RecurChangeLogData) => (
            <div>
                {row.ip_address}
            </div>
      ),
    },

  ]

  if (loadingData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '200px' }}>
          <Loader/>
      </div>
    )
  }

  return (
    <>
        <div style={{ padding: '1px', borderBottom: '1px solid #ccc', marginBottom: '35px' }} className='legend'>
          <h1>Recurring Payment Log for ID {currentRecord.id}</h1>
        </div>
        <Fieldset>
            <Legend title="Current Details"/>
            <Table columns={recurringChangeLogColumns} data={currentRecordProcessed} cssStyles={summaryTableStyle}/>
        </Fieldset>
        <Fieldset>
            <Legend title="Details prior to most recent change and time, user, and source of change"/>
            <Table columns={recurringChangeLogColumns} data={changeLogData} cssStyles={summaryTableStyle} />
        </Fieldset>
        {showSurchargeInfo && (<p style={{ textAlign: 'left' }}> * Amount does not include surcharge</p>)}

        <div style={{ display: 'flex', justifyContent: 'right', alignItems: 'right' }}>
            <PrimaryButton onClick={() => setIsChangeLogButtonClicked(false)}>
                Close
            </PrimaryButton>
        </div>
    </>
  )
}

export default RecurringChangeLog
